import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from './intrivity_app.js';
import { t } from './i18n.js';

// Administrative audit trail (finding F-06).
//
// Records the administrative actions that touch someone else's data: starting
// and ending a masquerade session, changing a role, and reading other people's
// reports in bulk. Entries go to the `audit_log` collection, which the
// Firestore rules make append-only -- create is allowed, update and delete are
// denied to everyone including superadmins, so nothing can be edited away
// through the application.
//
// WHAT THIS IS NOT: these entries are written by the browser, so they record
// what the application did, not what was physically possible. Someone with
// direct console or API access can act without generating an entry, and could
// delete entries outside the rules. Making role changes genuinely tamper-proof
// wants a Firestore onDocumentUpdated trigger writing the entry server-side,
// where the client cannot skip it. Treat this as an operational record, and say
// so plainly rather than overclaiming it as forensic evidence.
//
// Recording must never break the action it describes: every call is
// fire-and-forget and failures are logged to the console only. An admin losing
// the ability to fix a role because an audit write failed would be worse than
// the missing entry.

export const AUDIT = {
    MASQUERADE_START: 'masquerade.start',
    MASQUERADE_END: 'masquerade.end',
    ROLE_CHANGE: 'role.change',
    GROUP_ROLE_CHANGE: 'role.group.change',
    BULK_REPORT_READ: 'report.bulk_read',
    AI_CACHE_CLEAR: 'ai_summary.cache_clear',
    USER_DATA_REMOVE_GROUPS: 'user_data.remove_groups',
};

// The actor is taken from the Firebase auth token, never from a caller or from
// user_profile. Two reasons: the Firestore rule compares actor against
// request.auth.token.email, so anything else silently fails the write; and
// user_profile is application state that can be incomplete, whereas the token
// is the identity the request is actually made with.
//
// Masquerading does not re-authenticate -- it only changes app state -- so the
// token still names the real administrator throughout a masquerade session,
// which is exactly who the trail should attribute the action to.
export async function recordAudit(event, { subject, detail } = {}) {
    if (!event) return;
    const actor = (auth && auth.currentUser && auth.currentUser.email) || null;
    if (!actor) {
        console.warn(`Audit skipped for "${event}": no authenticated user.`);
        return;
    }

    // F-05. How the administrator authenticated is part of what happened, and
    // most privileged actions -- masquerade, role changes, report reads -- run
    // in the browser against Security Rules rather than through the guarded
    // HTTP endpoints, so recording it only server-side would miss nearly all
    // of them.
    //
    // Read from providerData rather than the ID token: it needs no async call
    // and cannot fail, and auditing must never break the action it describes.
    // It is a weaker source -- a client could lie -- but this entry is already
    // client-written, so it adds no new trust assumption. Treat it as
    // operational evidence of how administrators actually sign in, which is
    // what it is for: deciding whether PRIVILEGED_AUTH_POLICY can be raised
    // from "report" to "sso" without locking anyone out.
    let signIn = null;
    try {
        const pd = (auth.currentUser.providerData || []).map((p) => p && p.providerId).filter(Boolean);
        signIn = { providers: pd, password_only: pd.length > 0 && pd.every((x) => x === 'password') };
    } catch (e) {
        signIn = null;
    }
    try {
        await addDoc(collection(db, 'audit_log'), {
            event,
            actor,
            subject: subject || null,
            detail: detail || null,
            at: serverTimestamp(),
            // Client clock as well as the server stamp: a large gap between
            // them is itself worth noticing.
            clientAt: new Date().toISOString(),
            userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || null,
            signIn,
        });
    } catch (e) {
        console.error(`Audit write failed for "${event}":`, e);
    }
}

// Human-readable label for the viewer, in the reader's language
// (audit.event.<event>). Events without one -- server-written ones, say --
// show their raw name.
const LABELLED_EVENTS = new Set(Object.values(AUDIT));
export const auditLabel = (event) => (LABELLED_EVENTS.has(event) ? t(`audit.event.${event}`) : event);
